<template>
  <div>
    <section v-if="contentReady" class="notes-hero">
      <div class="notes-hero-copy">
        <span class="notes-eyebrow">{{ intro.eyebrow }}</span>
        <h1>{{ intro.title }}</h1>
        <p v-if="intro.description">{{ intro.description }}</p>
        <div v-if="intro.tags.length" class="notes-hero-meta">
          <span v-for="tag in intro.tags" :key="tag">{{ tag }}</span>
        </div>
      </div>

      <div class="notes-hero-panel">
        <div class="notes-hero-stat">
          <strong>{{ paddedCount(totalNotes) }}</strong>
          <span>篇文章</span>
        </div>
        <div class="notes-hero-stat">
          <strong>{{ paddedCount(totalCategories) }}</strong>
          <span>个分类</span>
        </div>
        <div class="notes-hero-stat">
          <strong>{{ paddedCount(totalCodeBlocks) }}</strong>
          <span>个代码片段</span>
        </div>
      </div>
    </section>

    <section v-if="contentReady" class="notes-toolbar">
      <div class="notes-tags">
        <button
          type="button"
          :class="{ 'is-active': currentFilter === 'all' }"
          @click="setFilter('all')"
        >
          全部
        </button>
        <button
          v-for="category in categories"
          :key="category.slug"
          type="button"
          :class="{ 'is-active': currentFilter === category.slug }"
          @click="setFilter(category.slug)"
        >
          {{ category.title }}
        </button>
      </div>

      <RouterLink
        class="notes-toolbar-link"
        :to="sampleNote ? `/tech-notes/${sampleNote.slug}` : '/tech-notes'"
      >
        {{ sampleNote ? "查看最新文章" : "查看示例文章" }}
      </RouterLink>
    </section>

    <section class="notes-list">
      <div v-if="!contentReady" class="notes-empty-state">
        <h2>内容加载中</h2>
        <p>正在从服务器同步技术手记内容，请稍等一下。</p>
      </div>

      <div v-else-if="!filteredNotes.length" class="notes-empty-state">
        <h2>这里还没有文章</h2>
        <p>你可以稍后继续补充内容，我也可以继续帮你把这部分做成正式发布版本。</p>
      </div>

      <article v-else v-for="item in pagedNotes" :key="item.slug" class="note-row-card">
        <RouterLink class="note-row-cover" :to="`/tech-notes/${item.slug}`">
          <img :src="item.image" :alt="item.title">
        </RouterLink>

        <div class="note-row-body">
          <div class="note-row-meta">
            <span>{{ item.categoryTitle }}</span>
            <span>{{ item.date }}</span>
          </div>
          <h2>
            <RouterLink :to="`/tech-notes/${item.slug}`">
              {{ item.title }}
            </RouterLink>
          </h2>
          <p v-if="item.summary">{{ item.summary }}</p>
          <div class="note-row-footer">
            <span class="note-row-reading">{{ item.reading }}</span>
            <RouterLink class="note-row-link" :to="`/tech-notes/${item.slug}`">
              阅读全文
            </RouterLink>
          </div>
        </div>
      </article>
    </section>

    <nav v-if="filteredNotes.length" class="notes-pagination" aria-label="技术手记分页">
      <div class="notes-page-summary">
        第 {{ currentPage }} / {{ totalPages }} 页，共 {{ filteredNotes.length }} 篇
      </div>
      <div class="notes-page-actions">
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === 1"
          @click="setPage(1)"
        >
          第一页
        </button>
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === 1"
          @click="setPage(currentPage - 1)"
        >
          上一页
        </button>
        <button
          v-for="page in totalPages"
          :key="page"
          type="button"
          class="notes-page-number"
          :class="{ 'is-active': page === currentPage }"
          @click="setPage(page)"
        >
          {{ page }}
        </button>
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === totalPages"
          @click="setPage(currentPage + 1)"
        >
          下一页
        </button>
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === totalPages"
          @click="setPage(totalPages)"
        >
          最后一页
        </button>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { computed, ref, watch, watchEffect } from "vue";
import { RouterLink } from "vue-router";
import { useContentStore } from "@/store/contentStore";

const notesPerPage = 10;
const currentPage = ref(1);
const currentFilter = ref("all");
const { techNotes, moduleIntros, contentReady } = useContentStore();

const intro = computed(() => moduleIntros.value.techNotes);

const categories = computed(() =>
  Array.from(
    new Map(
      techNotes.value.map((item) => [
        item.categorySlug,
        { slug: item.categorySlug, title: item.categoryTitle },
      ])
    ).values()
  )
);

const filteredNotes = computed(() => {
  if (currentFilter.value === "all") {
    return techNotes.value;
  }

  return techNotes.value.filter((item) => item.categorySlug === currentFilter.value);
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredNotes.value.length / notesPerPage))
);

const pagedNotes = computed(() => {
  const startIndex = (currentPage.value - 1) * notesPerPage;
  return filteredNotes.value.slice(startIndex, startIndex + notesPerPage);
});

const totalNotes = computed(() => techNotes.value.length);
const totalCategories = computed(() => categories.value.length);
const totalCodeBlocks = computed(() =>
  techNotes.value.reduce((sum, note) => sum + (note.codeBlocks || 0), 0)
);
const sampleNote = computed(() => techNotes.value[0] || null);

const paddedCount = (value) => String(value).padStart(2, "0");

const setFilter = (filter) => {
  currentFilter.value = filter;
  currentPage.value = 1;
};

const setPage = (page) => {
  currentPage.value = Math.min(totalPages.value, Math.max(1, page));
};

watch(filteredNotes, () => {
  if (currentPage.value > totalPages.value) {
    currentPage.value = 1;
  }
});

watchEffect(() => {
  document.title = "圣域空间 | 技术手记";
});
</script>
